/**
 * NotificationSettingsPage — J-3. 알림 설정
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Page, PageHeader, Skeleton, Switch } from '@indipia/ui-core';
import { fetchNotiSettings, updateNotiSettings } from '../api/noti-settings';
import type { NotiSettings } from '../api/noti-settings';

// ── 알림 항목 행 ──────────────────────────────────────────────────────────────

function NotiRow({
  title,
  description,
  on,
  onToggle,
}: {
  title: string;
  description: string;
  on: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-border px-6 py-5 last:border-b-0">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-text">{title}</p>
        <p className="mt-0.5 text-xs text-text-muted">{description}</p>
      </div>
      <Switch checked={on} onChange={onToggle} aria-label={title} />
    </div>
  );
}

// ── 메인 페이지 ───────────────────────────────────────────────────────────────

export function NotificationSettingsPage() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['noti-settings'],
    queryFn: fetchNotiSettings,
  });

  const mutation = useMutation({
    mutationFn: updateNotiSettings,
    onMutate: async (patch) => {
      await queryClient.cancelQueries({ queryKey: ['noti-settings'] });
      const prev = queryClient.getQueryData<NotiSettings>(['noti-settings']);
      if (prev) queryClient.setQueryData(['noti-settings'], { ...prev, ...patch });
      return { prev };
    },
    onError: (_err, _patch, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['noti-settings'], ctx.prev);
      toast.error('설정 변경에 실패했습니다.');
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['noti-settings'] }),
  });

  function toggle(key: keyof Pick<NotiSettings, 'smsYn' | 'kakaoYn' | 'emailYn'>) {
    if (!data) return;
    mutation.mutate({ [key]: data[key] === 'Y' ? 'N' : 'Y' });
  }

  return (
    <Page width="md">
      <PageHeader title="알림 설정" subtitle="알림 수신 채널을 개별 설정할 수 있어요" />

      {/* 채널 카드 */}
      <div className="overflow-hidden rounded-xl border border-border bg-surface-raised">
        {isLoading ? (
          // 행 골격(border·padding)을 그대로 쓰고 내용만 스켈레톤으로 — 데이터 도착 시 레이아웃이 튀지 않는다 (D-07 §17)
          [0, 1, 2].map((i) => (
            <div key={i} className="border-b border-border px-6 py-5 last:border-b-0">
              <Skeleton height={36} />
            </div>
          ))
        ) : (
          <>
            <NotiRow
              title="SMS 알림"
              description="문자 메시지로 주요 알림을 받아요"
              on={data?.smsYn === 'Y'}
              onToggle={() => toggle('smsYn')}
            />
            <NotiRow
              title="알림톡"
              description="카카오 알림톡으로 알림을 받아요"
              on={data?.kakaoYn === 'Y'}
              onToggle={() => toggle('kakaoYn')}
            />
            <NotiRow
              title="이메일 알림"
              description="이메일로 알림 및 정기 리포트를 받아요"
              on={data?.emailYn === 'Y'}
              onToggle={() => toggle('emailYn')}
            />
          </>
        )}
      </div>
    </Page>
  );
}
